import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Editor,
    EditorProvider,
    Toolbar,
    BtnBold,
    BtnItalic,
    BtnUnderline,
    BtnNumberedList,
    BtnBulletList,
    BtnLink
} from "react-simple-wysiwyg";
import { toast } from "react-toastify";
import { useApp } from "../../AppContext";
import { IMG_BASE_URL } from "../../Helpers";
import {
    fetchCategories,
    fetchAuthors,
    fetchBookById,
    addBook,
    updateBook
} from "../../apiCalls";
export default function AddorEditBook() {

    const { books, oldBooks, setBooks, setOldBooks } = useApp();
    const { id } = useParams();
    const navigate = useNavigate();

    const isEditMode = !!id;

    const [categories, setCategories] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(isEditMode);

    const [book, setBook] = useState({
        book_name: "",
        category_id: "",
        mrp: "",
        discount: "",
        description: "",
        image: "",
        author_ids: [],
        pages: "",
        published_year: "",
        binding: "",
        amazon_link: "",
        flipkart_link: ""
    });

    const [imagePreview, setImagePreview] = useState(null);

    //////////////////////////////////////////////////////
    // Load dropdown data
    useEffect(() => {
        const loadDropdownData = async () => {
            try {
                const [catRes, authRes] = await Promise.all([
                    fetchCategories(),
                    fetchAuthors()
                ]);
                setCategories(catRes || []);
                setAuthors(authRes || []);
            } catch (error) {
                console.error("Error loading form data", error);
            }
        };
        loadDropdownData();
    }, []);


    //////////////////////////////////////////////////////
    // Same logic as ViewBook
    useEffect(() => {
        if (!isEditMode) return;

        const allBooks = [...books, ...oldBooks];
        const foundBook = allBooks.find(
            (item) => item.book_id.toString() === id
        );

        if (foundBook) {
            populateBook(foundBook);
            setLoading(false);
        } else {
            fetchBookById();
        }

    }, [id, books, oldBooks]);

    //////////////////////////////////////////////////////
    const fetchBookById = async () => {
        try {
            const response = await fetchBookById(id);
            if (response) {
                populateBook(response);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    //////////////////////////////////////////////////////
    const populateBook = (data) => {
        setBook({
            book_name: data.book_name || "",
            category_id: data.category_id || "",
            mrp: data.mrp || "",
            discount: data.discount_percent || "",
            description: data.description || "",
            image: data.image || "",
            author_ids: data.authors
                ? data.authors.map(a => Number(a.author_id))
                : [],
            pages: data.no_of_pages || "",
            published_year: data.published_year || "",
            binding: data.binding || "",
            amazon_link: data.amazon_link || "",
            flipkart_link: data.flipkart_link || ""
        });

        if (data.image) {
            setImagePreview(
                IMG_BASE_URL + "books/" + data.image
            );
        }
    };


    //////////////////////////////////////////////////////
    const handleChange = (e) => {
        const { name, value } = e.target;
        setBook(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBook(prev => ({
                ...prev,
                image: file
            }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleAuthorCheckbox = (authorId) => {
        const idNum = Number(authorId);

        setBook(prev => {
            const exists = prev.author_ids.includes(idNum);
            return {
                ...prev,
                author_ids: exists
                    ? prev.author_ids.filter(id => id !== idNum)
                    : [...prev.author_ids, idNum]
            };
        });
    };

    //////////////////////////////////////////////////////
    const isFormValid = () => {
        return (
            book.book_name.trim() &&
            book.category_id &&
            book.mrp &&
            book.discount &&
            book.description.trim() &&
            book.author_ids.length > 0 &&
            (isEditMode || book.image)
        );
    };

    //////////////////////////////////////////////////////
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid()) {
            toast.error("Please fill all mandatory fields");
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();

            // Convert author_ids array to comma-separated string
            const authorString = book.author_ids.join(",");
            const fileName = book.image instanceof File ? book.image.name : book.image;

            formData.append("name", book.book_name);
            formData.append("category_id", book.category_id);
            formData.append("mrp", book.mrp);
            formData.append("discount_percent", book.discount);
            formData.append("description", book.description);
            formData.append("no_of_pages", book.pages);
            formData.append("published_year", book.published_year);
            formData.append("binding", book.binding);
            formData.append("amazon_link", book.amazon_link);
            formData.append("flipkart_link", book.flipkart_link);

            formData.append("author_id", authorString); // ✅ API format
            formData.append("image_filename", fileName);

            if (book.image instanceof File) {
                formData.append("image_file", book.image);
            }

            const response = isEditMode
                ? await updateBook(id, formData)
                : await addBook(formData);

            if (response && response.status) {
                // 1. Get full author objects from our local 'authors' list to keep Store UI consistent
                const selectedAuthors = authors.filter(a => book.author_ids.includes(Number(a.author_id)));
                const bookId = id || response.inserted_id || response.book_id;

                // 2. Construct the object EXACTLY how the store expects it (check keys!)
                const updatedEntry = {
                    book_id: id || response.inserted_id, // Ensure we have an ID
                    book_name: book.book_name,
                    category_id: book.category_id,
                    mrp: book.mrp,
                    discount_percent: book.discount, // Mapping back to store key
                    description: book.description,
                    no_of_pages: book.pages,        // Mapping back to store key
                    published_year: book.published_year,
                    binding: book.binding,
                    amazon_link: book.amazon_link,
                    flipkart_link: book.flipkart_link,
                    image: fileName,
                    authors: selectedAuthors
                };

                // 3. Update the correct list in AppContext
                const isOldBook = String(book.category_id) === "2";

                if (isEditMode) {
                    // Remove from both lists first (in case category changed)
                    const filteredBooks = books.filter(b => b.book_id.toString() !== id.toString());
                    const filteredOldBooks = oldBooks.filter(b => b.book_id.toString() !== id.toString());

                    if (isOldBook) {
                        setOldBooks([updatedEntry, ...filteredOldBooks]);
                        setBooks(filteredBooks);
                    } else {
                        setBooks([updatedEntry, ...filteredBooks]);
                        setOldBooks(filteredOldBooks);
                    }
                } else {
                    // Add Mode
                    if (isOldBook) {
                        setOldBooks([updatedEntry, ...oldBooks]);
                    } else {
                        setBooks([updatedEntry, ...books]);
                    }
                }

                toast.success(isEditMode ? "Book updated successfully" : "Book added successfully");
                navigate(`/view-book/${bookId}`);

            }

        } catch (error) {
            console.log(error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    //////////////////////////////////////////////////////
    if (loading && isEditMode) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    //////////////////////////////////////////////////////
    return (
        <>
            <div className="page-title-container">
                <h2>
                    {isEditMode ? "Edit Book" : "Add Book"}
                    <span>
                        {isEditMode
                            ? "Update book details"
                            : "Create a new book"}
                    </span>
                </h2>
            </div>

            <div className="container mt-4">
                <form onSubmit={handleSubmit}>

                    {/* Book Name */}
                    <div className="mb-3">
                        <label>Book Name *</label>
                        <input
                            type="text"
                            className="form-control"
                            name="book_name"
                            value={book.book_name}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="row">
                        {/* Category */}
                        <div className="col-md-4 mb-3">
                            <label>Category *</label>
                            <select
                                className="form-control"
                                name="category_id"
                                value={book.category_id}
                                onChange={handleChange}
                            >
                                <option value="">Select Category</option>
                                {categories.map(cat => (
                                    <option key={cat.category_id} value={cat.category_id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* MRP */}
                        <div className="col-md-4 mb-3">
                            <label>MRP *</label>
                            <input
                                type="number"
                                className="form-control"
                                name="mrp"
                                value={book.mrp}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Discount */}
                        <div className="col-md-4 mb-3">
                            <label>Discount (%) *</label>
                            <input
                                type="number"
                                className="form-control"
                                name="discount"
                                value={book.discount}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    {/* Description */}
                    <div className="mb-3">
                        <label>Description *</label>
                        <EditorProvider>
                            <Editor
                                value={book.description}
                                onChange={(e) =>
                                    setBook(prev => ({
                                        ...prev,
                                        description: e.target.value
                                    }))
                                }
                            >
                                <Toolbar>
                                    <BtnBold />
                                    <BtnItalic />
                                    <BtnUnderline />
                                    <BtnNumberedList />
                                    <BtnBulletList />
                                    <BtnLink />
                                </Toolbar>
                            </Editor>
                        </EditorProvider>
                    </div>



                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">No of Pages</label>
                            <input
                                type="number"
                                className="form-control"
                                name="pages"
                                value={book.pages}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="form-label">Published Year</label>
                            <input
                                type="number"
                                className="form-control"
                                name="published_year"
                                value={book.published_year}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Binding</label>
                            <input
                                type="text"
                                className="form-control"
                                name="binding"
                                value={book.binding}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Amazon Link</label>
                        <input
                            type="text"
                            className="form-control"
                            name="amazon_link"
                            value={book.amazon_link}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Flipkart Link</label>
                        <input
                            type="text"
                            className="form-control"
                            name="flipkart_link"
                            value={book.flipkart_link}
                            onChange={handleChange}
                        />
                    </div>
                    {/* Authors */}
                    <div className="mb-3">
                        <label>Authors *</label>
                        <div>
                            {authors.map(author => (
                                <div key={author.author_id}>
                                    <input
                                        type="checkbox"
                                        checked={book.author_ids.includes(
                                            Number(author.author_id)
                                        )}
                                        onChange={() =>
                                            handleAuthorCheckbox(author.author_id)
                                        }
                                    />
                                    <label className="ms-2">
                                        {author.author_name}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Image */}
                    <div className="mb-3">
                        <label>Book Image *</label>
                        <input
                            type="file"
                            className="form-control"
                            onChange={handleImageChange}
                        />
                        {imagePreview && (
                            <img
                                src={imagePreview}
                                alt="preview"
                                width="500"
                                className="mt-2"
                            />
                        )}
                    </div>
                    {/* Binding */}

                    {/* Submit */}
                    <button
                        type="submit"
                        className="btn btn-secondary save-btn btn-sm"
                        disabled={!isFormValid() || loading}
                    >
                        {isEditMode ? "Update Book" : "Add Book"}
                    </button>

                </form>
            </div>
        </>
    );
}